
'use client';

import { useState, useRef, FormEvent } from 'react';
import { LoaderCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { SectionWrapper } from '../section-wrapper';
import Link from 'next/link';
import { Label } from '../ui/label';

function WaitingListForm() {
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();
    const formRef = useRef<HTMLFormElement>(null);
    
    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData(e.currentTarget);
        const body = Object.fromEntries(formData.entries());
        
        try {
            const res = await fetch('/api/waiting-list', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            const data = await res.json();

            if (res.ok) {
                toast({
                    title: '¡Te has unido!',
                    description: data.message,
                });
                if (data.sheetError) {
                    toast({
                        title: 'Error al guardar en Google Sheet',
                        description: data.sheetError,
                        variant: 'destructive',
                    });
                }
                formRef.current?.reset();
            } else {
                toast({
                    title: 'Error en el registro',
                    description: data.message || 'Ocurrió un error al registrarte.',
                    variant: 'destructive',
                });
            }
        } catch (error) {
            console.error('Error al unirse a la lista de espera:', error);
            toast({
                title: 'Error de Red',
                description: 'No se pudo conectar con el servidor. Inténtalo más tarde.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
      <form 
          ref={formRef}
          onSubmit={handleSubmit}
          className="grid gap-4 text-left"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">Nombre *</Label>
            <Input id="firstName" name="firstName" placeholder="Tu nombre" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Apellidos *</Label>
            <Input id="lastName" name="lastName" placeholder="Tus apellidos" required />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" name="email" type="email" placeholder="tu@email.com" required />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Input id="company" name="company" placeholder="Nombre de tu empresa" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="jobTitle">Cargo</Label>
            <Input id="jobTitle" name="jobTitle" placeholder="Tu puesto de trabajo" />
          </div>
        </div>

        {/* Campo Honeypot para engañar a los bots */}
        <div className="opacity-0 absolute h-0 w-0 z-[-1]">
            <label htmlFor="honeypot">No rellenes este campo si eres humano</label>
            <input type="text" id="honeypot" name="honeypot" tabIndex={-1} autoComplete="off" />
        </div>
        
         <div className="text-center text-xs text-muted-foreground mt-2">
              <p>
                  Al pulsar "Apúntate a la lista de espera", aceptas nuestra{' '}
                  <Link href="/politica-privacidad" className="underline hover:text-primary" target="_blank">
                      Política de Privacidad
                  </Link>.
              </p>
          </div>

        <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 font-bold text-lg">
          {loading ? <LoaderCircle className="animate-spin" /> : "APÚNTATE A LA LISTA DE ESPERA"}
        </Button>
      </form>
    );
}

export function WaitingListSection() {  
  return (
    <SectionWrapper id="waiting-list" className="bg-card text-card-foreground">
      <div className="container grid items-center justify-center gap-8 px-4 text-center md:px-6">
        <div className="space-y-3">
          <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl font-headline">
            <span className="block text-card-foreground">SÉ EL PRIMERO EN</span>
            <span className="block text-primary">CONSEGUIR TU ENTRADA</span>
          </h1>
          <p className="mx-auto max-w-[700px] text-card-foreground/80 md:text-xl/relaxed">
            La próxima edición de Sin Gluten Experience Day será próximamente en Valencia. Únete a nuestra lista de espera para conseguir tu entrada.
          </p>
        </div>
        <div className="mx-auto w-full max-w-2xl space-y-6">
            <div className="grid md:grid-cols-2 gap-6 text-left mb-8">
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/10">
                    <h3 className="font-bold text-primary mb-2">Acceso Prioritario</h3>
                    <p className="text-sm text-card-foreground/70">Recibirás el enlace de compra 24 horas antes de que las entradas salgan a la venta general.</p>
                </div>
                <div className="bg-primary/5 p-4 rounded-lg border border-primary/10">
                    <h3 className="font-bold text-primary mb-2">Unidades Limitadas</h3>
                    <p className="text-sm text-card-foreground/70">Asegura tu plaza en un evento con aforo controlado para garantizar la mejor experiencia.</p>
                </div>
            </div>
            
            <div className="space-y-4 text-left mb-8 p-6 bg-secondary/10 rounded-xl">
              <h2 className="text-xl font-bold font-headline">¿Por qué unirse a la lista?</h2>
              <p className="text-card-foreground/80">
                Al unirte a nuestra lista de espera, no solo te aseguras de no perderte la apertura de taquilla, sino que formarás parte de una comunidad privilegiada que recibirá contenido exclusivo, recetas en primicia y descuentos de nuestras marcas colaboradoras durante todo el año.
              </p>
            </div>

            <WaitingListForm />
        </div>
        <div className="mt-16 text-left space-y-8 max-w-4xl mx-auto border-t border-border/50 pt-12">
            <h2 className="text-2xl font-bold font-headline">Lo que puedes esperar de nuestra comunidad</h2>
            <div className="grid md:grid-cols-2 gap-12">
                <div className="space-y-4">
                    <h3 className="text-lg font-bold text-primary">Contenido Exclusivo</h3>
                    <p className="text-card-foreground/70">
                        No te inundaremos con correos irrelevantes. Solo recibirás información de alto valor: desde guías de viaje para celíacos hasta entrevistas con nutricionistas especializados y chefs que están revolucionando la cocina sin gluten en España. Queremos que tu espera hasta el próximo evento sea productiva y deliciosa.
                    </p>
                </div>
                <div className="space-y-4">
                    <h3 className="text-lg font-bold text-primary">Precios Especiales</h3>
                    <p className="text-card-foreground/70">
                        La lista de espera tiene premio. Además del acceso prioritario, los inscritos podrán optar a una tarifa "Early Bird" con un descuento significativo sobre el precio final de la entrada. Es nuestra forma de agradecer tu interés temprano en el Sin Gluten Experience Day y tu apoyo a la normalización de la vida sin gluten.
                    </p>
                </div>
            </div>
            <div className="bg-primary/5 p-6 rounded-xl text-center">
                <p className="text-card-foreground/80 italic">
                    "Más que un evento, estamos construyendo el movimiento sin gluten más potente de habla hispana. Tu inscripción es el primer paso para formar parte de algo realmente grande."
                </p>
            </div>
        </div>
      </div>
    </SectionWrapper>
  );
};
